import {
  saveModuleQuizProgressApiV1ModulesModuleIdQuizSaveProgressPost,
  startModuleQuizApiV1ModulesModuleIdQuizStartPost,
  submitModuleQuizApiV1ModuleQuizzesSubmitPost,
} from "@/src/api/sdk.gen";

import { Suspense, use, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "@/src/lib/next-compat";
import { useQueryClient } from "@tanstack/react-query";
import { courseExtrasQueryKey, invalidateCourseCore } from "@/lib/student/course-core-query";
import { CheckCircle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { ModuleQuizSubmissionResult } from "@/lib/module-quiz/types";
import {
  parseModuleQuizStartResponse,
  type ModuleQuizClientDisplayQuestion,
  type ModuleQuizInitResult,
} from "@/lib/module-quiz/client-quiz";
import {
  MODULE_QUIZ_PASS_PERCENT,
} from "@/lib/module-quiz/constants";

type StartResponse = {
  attemptUuid: string;
  resumed: boolean;
  totalQuestions: number;
  title: string;
  questions: unknown[];
  reviewMode?: boolean;
  passed?: boolean;
  score?: number;
};

function ModuleQuizContent({
  params,
}: {
  params?: Promise<{ id: string; moduleId: string }>;
} = {}) {
  const routerParams = useParams<{ id: string; moduleId: string }>();
  const courseId = routerParams.id || "";
  const moduleId = routerParams.moduleId || "";
  const queryClient = useQueryClient();

  const [attemptUuid, setAttemptUuid] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ModuleQuizClientDisplayQuestion[]>([]);
  const [title, setTitle] = useState("Module Quiz");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewScore, setReviewScore] = useState<number | null>(null);
  const [previousAttemptScore, setPreviousAttemptScore] = useState<number | null>(null);
  const [previousAttemptWrongCount, setPreviousAttemptWrongCount] = useState<number | null>(null);
  const [previousAttemptPassed, setPreviousAttemptPassed] = useState<boolean | null>(null);
  const [passThreshold, setPassThreshold] = useState<number>(80);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [isSubmittingQuiz, setIsSubmittingQuiz] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] =
    useState<ModuleQuizSubmissionResult | null>(null);

  const startQuiz = async (signal?: AbortSignal) => {
    const { data, error } = await startModuleQuizApiV1ModulesModuleIdQuizStartPost({
      path: { module_id: Number(moduleId) },
      signal,
    });
    if (error || !data) {
      const errObj = error as { error?: string };
      throw new Error(errObj?.error ?? "Failed to start module quiz.");
    }
    return data as unknown as StartResponse;
  };

  const applyQuizInitialization = (result: ModuleQuizInitResult) => {
    if (!result.ok) {
      setError(result.error);
      setQuestions([]);
      return;
    }
    setError(null);
    setQuestions(result.questions);
    setAttemptUuid(result.attemptUuid);
    setTitle(result.title);
    setReviewMode(result.reviewMode);
    setReviewScore(result.score);
    setPreviousAttemptScore(result.previousAttemptScore ?? null);
    setPreviousAttemptWrongCount(result.previousAttemptWrongCount ?? null);
    setPreviousAttemptPassed(result.previousAttemptPassed ?? null);
    if (typeof result.passThreshold === "number") {
      setPassThreshold(result.passThreshold);
    }
    if (result.savedAnswers) {
      setSelectedAnswers(result.savedAnswers);
    }
    const initialIndex =
      typeof result.currentQuestion === "number" && result.currentQuestion >= 0
        ? Math.min(result.currentQuestion, result.questions.length - 1)
        : 0;
    setCurrentQuestion(initialIndex);
    setSelectedOption(result.savedAnswers ? result.savedAnswers[initialIndex] ?? null : null);
  };

  const initializeQuiz = async (signal?: AbortSignal) => {
    const data = await startQuiz(signal);
    return parseModuleQuizStartResponse(data);
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      try {
        const result = await initializeQuiz(controller.signal);
        if (!active) return;
        applyQuizInitialization(result);
      } catch (err) {
        if (!active) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Failed to load module quiz.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [moduleId]);

  useEffect(() => {
    if (!showResults || !submissionResult?.passed) return;
    void invalidateCourseCore(queryClient, courseId);
    if (submissionResult.levelCompleted) {
      void queryClient.invalidateQueries({ queryKey: ["certificates-me"] });
    }
  }, [showResults, submissionResult, courseId, queryClient]);

  const handleOptionSelect = (index: number) => {
    setSelectedOption(index);
  };

  const handleNext = async () => {
    if (selectedOption === null) return;

    const updatedAnswers = {
      ...selectedAnswers,
      [currentQuestion]: selectedOption,
    };

    setSelectedAnswers(updatedAnswers);

    if (currentQuestion < questions.length - 1) {
      const nextIndex = currentQuestion + 1;
      setCurrentQuestion(nextIndex);
      setSelectedOption(updatedAnswers[nextIndex] ?? null);

      // Save previous answers progressively to backend DB
      if (attemptUuid) {
        try {
          await saveModuleQuizProgressApiV1ModulesModuleIdQuizSaveProgressPost({
            path: { module_id: Number(moduleId) },
            body: {
              attempt_uuid: attemptUuid,
              answers: updatedAnswers,
              current_question: nextIndex,
            },
          });
        } catch (saveErr) {
          console.warn("Could not save progress to DB:", saveErr);
        }
      }
      return;
    }

    if (!attemptUuid) return;

    const answers: Record<string, number> = {};
    for (const [qi, ai] of Object.entries(updatedAnswers)) {
      answers[`questionIndex_${qi}`] = ai;
    }

    setSubmissionError(null);
    setIsSubmittingQuiz(true);
    try {
      const { data: resData, error } = await submitModuleQuizApiV1ModuleQuizzesSubmitPost({
        body: { attempt_uuid: attemptUuid, answers } as any,
      });
      const data = (resData || error || {}) as ModuleQuizSubmissionResult & {
        error?: string;
      };
      if (error) {
        throw new Error(data.error ?? "Failed to submit module quiz.");
      }
      setSubmissionResult(data);
      setShowResults(true);
      void queryClient.invalidateQueries({ queryKey: courseExtrasQueryKey(courseId) });
    } catch (err) {
      setSubmissionError(err instanceof Error ? err.message : "Submit failed.");
      setShowResults(true);
    } finally {
      setIsSubmittingQuiz(false);
    }
  };

  const handleRetry = async () => {
    setLoading(true);
    setShowResults(false);
    setSubmissionResult(null);
    setSubmissionError(null);
    setCurrentQuestion(0);
    setSelectedOption(null);
    setSelectedAnswers({});
    setError(null);
    setReviewMode(false);
    setReviewScore(null);
    try {
      const result = await initializeQuiz();
      applyQuizInitialization(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to restart quiz.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Module Quiz Unavailable</h2>
          <p className="text-gray-600 mb-6">{error ?? "No questions available."}</p>
          <Link
            to={`/courses/${courseId}`}
            className="inline-flex px-6 py-3 bg-primary text-white font-bold rounded-xl"
          >
            Back to Course
          </Link>
        </div>
      </div>
    );
  }

  if (reviewMode) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Module Quiz · Review</p>
              <h1 className="text-xl font-bold text-gray-900">{title}</h1>
              {reviewScore != null && (
                <p className="text-xs text-gray-600 mt-1">
                  {(() => {
                    const scorePct = Math.round(reviewScore * 100);
                    const correctCount = Math.round(reviewScore * questions.length);
                    const wrongCount = Math.max(0, questions.length - correctCount);
                    return wrongCount > 0
                      ? `Score: ${scorePct}% · You got ${wrongCount} ${wrongCount === 1 ? "question" : "questions"} wrong.`
                      : `Score: ${scorePct}% · 0 questions wrong.`;
                  })()}
                </p>
              )}
            </div>
            <span className="flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-bold text-green-700">
              <CheckCircle className="w-3.5 h-3.5" /> Passed
              {reviewScore != null ? ` · ${Math.round(reviewScore * 100)}%` : ""}
            </span>
          </div>

          <div className="space-y-4">
            {questions.map((q, idx) => {
              const chosenIdx = selectedAnswers[idx];
              const correctIdx = q.correctOptionIndex;
              return (
                <div key={q.id} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                  <p className="font-medium text-gray-900 mb-4">
                    {idx + 1}. {q.text}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((opt, optIdx) => {
                      const isCorrect = correctIdx === optIdx;
                      const isChosen = chosenIdx === optIdx;
                      const classes = isCorrect
                        ? "border-green-500 bg-green-50 text-green-900"
                        : isChosen
                          ? "border-red-400 bg-red-50 text-red-900"
                          : "border-gray-100 text-gray-700";
                      return (
                        <div
                          key={optIdx}
                          className={`w-full text-left p-3 rounded-xl border-2 flex items-center justify-between gap-2 ${classes}`}
                        >
                          <span>{opt}</span>
                          {isCorrect ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" aria-label="Correct answer" />
                          ) : isChosen ? (
                            <XCircle className="w-4 h-4 text-red-500 shrink-0" aria-label="Your answer" />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  {q.explanation ? (
                    <p className="mt-4 text-sm text-gray-500 border-t border-gray-100 pt-3">{q.explanation}</p>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center">
            <Link
              to={`/courses/${courseId}`}
              className="inline-block px-8 py-3 border border-gray-200 font-bold rounded-xl text-center"
            >
              Back to Course
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const passed = submissionResult?.passed === true;
    const scorePct = Math.round((submissionResult?.score ?? 0) * 100);
    const passThresholdPct = Math.round((submissionResult?.pass_threshold ?? (passThreshold / 100)) * 100);
    const totalCount = submissionResult?.total_count || questions.length || 1;
    const correctCount = submissionResult?.correct_count ?? Math.round(((submissionResult?.score ?? 0) * totalCount));
    const wrongCount = Math.max(0, totalCount - correctCount);

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          {passed ? (
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          ) : (
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-rose-600" />
            </div>
          )}
          <h2 className="text-2xl font-bold mb-2 text-slate-900">
            {passed ? "Module Quiz Passed!" : "Quiz Failed"}
          </h2>
          {submissionError ? (
            <p className="text-red-600 mb-4">{submissionError}</p>
          ) : (
            <>
              {passed ? (
                <div className="mb-6 space-y-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold border border-green-200">
                    <CheckCircle2 className="w-4 h-4 text-green-600" /> Passed · {scorePct}%
                  </div>
                  <p className="text-gray-700 text-sm font-medium">
                    {wrongCount > 0
                      ? `You scored ${scorePct}% and got ${wrongCount} ${wrongCount === 1 ? "question" : "questions"} wrong.`
                      : `You scored ${scorePct}% with all questions answered correctly!`}
                  </p>
                </div>
              ) : (
                <div className="mb-6 space-y-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-100 text-rose-800 rounded-full text-xs font-bold border border-rose-200">
                    <XCircle className="w-4 h-4 text-rose-600" /> Not Passed · {scorePct}%
                  </div>
                  <p className="text-gray-700 text-sm font-medium">
                    You need <span className="font-bold text-slate-900">{passThresholdPct}%</span> to pass. You scored <span className="font-bold text-slate-900">{scorePct}%</span> and got <span className="font-bold text-slate-900">{wrongCount} {wrongCount === 1 ? "question" : "questions"} wrong</span>.
                  </p>
                </div>
              )}
              {passed && submissionResult?.levelCompleted && submissionResult.levelName ? (
                <div
                  className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900"
                  role="status"
                  aria-live="polite"
                >
                  <p className="font-semibold flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" aria-hidden />
                    {submissionResult.levelName} level complete
                  </p>
                  <p className="mt-1 text-indigo-700">
                    Your certificate is being prepared — you can return to the course to track progress.
                  </p>
                </div>
              ) : null}
              {passed && submissionResult?.currentStreak != null && submissionResult.currentStreak > 0 ? (
                <p className="text-sm text-orange-600 font-medium mb-4">
                  {submissionResult.currentStreak}-day streak!
                </p>
              ) : null}
            </>
          )}
          <div className="space-y-3 text-center">
            {!passed && !submissionError ? (
              <button
                type="button"
                onClick={() => void handleRetry()}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md transition-colors"
              >
                Try Again
              </button>
            ) : null}
            <Link
              to={`/courses/${courseId}`}
              className="inline-block px-8 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold rounded-xl transition-colors"
            >
              Back to Course
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      const prevIndex = currentQuestion - 1;
      setCurrentQuestion(prevIndex);
      setSelectedOption(selectedAnswers[prevIndex] ?? null);
    }
  };

  const q = questions[currentQuestion];
  if (!q) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <p className="text-sm text-gray-500">Module Quiz</p>
          <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-indigo-600 mt-1">
            Question {currentQuestion + 1} of {questions.length} · {passThreshold}% required to pass
          </p>
          {previousAttemptPassed === false && typeof previousAttemptScore === "number" && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
              <span className="font-bold">Previous attempt:</span> You need {passThreshold}% to pass. You scored {Math.round(previousAttemptScore)}% and got {previousAttemptWrongCount ?? 0} {(previousAttemptWrongCount ?? 0) === 1 ? "question" : "questions"} wrong.
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <p className="font-medium text-gray-900 mb-4">{q.text}</p>
          <div className="space-y-2">
            {q.options.map((opt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleOptionSelect(idx)}
                className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${
                  selectedOption === idx
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-100 hover:border-indigo-200"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            {currentQuestion > 0 ? (
              <button
                type="button"
                onClick={handlePrevious}
                className="px-5 py-2.5 border border-gray-300 font-semibold rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            ) : <div />}
            <button
              type="button"
              disabled={selectedOption === null || isSubmittingQuiz}
              onClick={() => void handleNext()}
              className="px-6 py-2.5 bg-primary text-white font-bold rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmittingQuiz ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : currentQuestion < questions.length - 1 ? (
                "Next"
              ) : (
                "Submit Quiz"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ModuleQuizPage(props?: {
  params?: Promise<{ id: string; moduleId: string }>;
}) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      }
    >
      <ModuleQuizContent params={props?.params} />
    </Suspense>
  );
}
